import React, { useEffect, useState, useRef, useContext } from 'react';
import { Box, TextField, Typography, CircularProgress, IconButton, Avatar, Snackbar, Alert } from '@mui/material';
import { Send as SendIcon, AttachFile as AttachFileIcon } from '@mui/icons-material';
import { API } from '../../controller/api';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { LanguageContext } from '../../controller/context/languageContext';
import HubIcon from '@mui/icons-material/Hub';
import { User } from '../ChatRoles/User';
import { System } from '../ChatRoles/System';
import { DrawerContext } from '../../controller/context/DrawerWidthContext';
import Cookies from 'js-cookie';
import { UpdateContext } from '../../controller/context/UpdateContext';
import { SelectedIDContext } from '../../controller/context/SelectedIdContext';
import { TitleContext } from '../../controller/context/TitleContext';
import FileChip from '../FileChip';
import FileRole from '../ChatRoles/FileRole';
import { useTheme } from '@emotion/react';
import { useTranslateButton } from '../../controller/context/TranslateButtonContext';

export const Chat = () => {
  const theme = useTheme();

  const { updateSelectedId } = useContext(SelectedIDContext);
  const { setTitle } = useContext(TitleContext);
  const { updateData } = useContext(UpdateContext);
  const { drawerWidth } = useContext(DrawerContext);
  const { language } = useContext(LanguageContext);
  const location = useLocation();
  const [translating, setTranslating] = useState(false)
  const { state } = location;
  const [errorMessage, setErrorMessage] = useState(null);
  const [sending, setSending] = useState(false);  // Loading state for sending message
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState(state?.messages || [state.initialPrompt]);
  const [content, setContent] = useState(state);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const fileSizeLimit = 5 * 1024 * 1024;  // 5MB file size limit
  const user = JSON.parse(Cookies.get('user'));
          const userId = user.id;
  const fileInputRef = useRef(null);  // Reference for file input
  const { showTranslateButton, setShowTranslateButton } = useTranslateButton(); // Access the context

  const messagesEndRef = useRef(null);  // Reference to scroll to bottom

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      const validFileTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  // DOCX
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',  // XLSX
        'text/csv',  // CSV
        'application/csv',  // Another CSV MIME type
      ];

      if (!validFileTypes.includes(selectedFile.type)) {
        setFileError('Unsupported file type. Please upload a PDF, DOCX, XLSX, or CSV file.');
        setShowSnackbar(true);
        fileInputRef.current.value = '';  // Clear the file input
      } else {
        setFileError('');  // Clear any previous error messages
        if (selectedFile && selectedFile.size > fileSizeLimit) {
          setFileError('File is too large (max 5MB).');
          setShowSnackbar(true);
          setFile(null);
        } else {
          setFile(selectedFile);
          setFileError('');
        }
        // Proceed with file handling logic here (e.g., upload the file to the server)
      }
    }
   
  };

  const translateContent = async () => {
    if(messages.length === 1){
      
    if (language === 'en') {
      // Reset to default English values
      setContent((prevContent) => ({
        ...prevContent,
        entryMessage: state.entryMessage,
        initialPrompt: state.initialPrompt,
      }));
      setMessages([{ role: 'system', content: state.initialPrompt }]);
    } else {
      setTranslating(true)
      try {
        // Send the app object and target language to the backend
        const response = await axios.post(`${API}/chat/translate`, {
          app: {
            entryMessage: state.entryMessage,
            initialPrompt: state.initialPrompt,
          },
          targetLanguage: language,
        });
  
        const translatedApp = response.data;
  
        // Update the content and messages with translated values
        setContent((prevContent) => ({
          ...prevContent,
          entryMessage: translatedApp.entryMessage,
          initialPrompt: translatedApp.initialPrompt,
        }));
        setMessages([{ role: 'system', content: translatedApp.initialPrompt }]);
        setTranslating(false)

      } catch (error) {
        setTranslating(false)

        console.error('Error translating content:', error);
      }
    }
  }
  };

  // Translate content on language change
  useEffect(() => {
    translateContent();
  }, [language]);

  const handleRemoveFile = () => {
    setFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    // If there is more than one message, hide the Translate button
    if (messages.length > 1) {
      setShowTranslateButton(false);
    } else {
      setShowTranslateButton(true);
    }
  }, [messages, setShowTranslateButton]);

  const sendMessage = async () => {
    let updatedMessages = [...messages, { role: 'user', content: message }];
  
    // If there's a file, add it as a separate message before sending
    if (file) {
      console.log(file.name);
      updatedMessages = [
        ...updatedMessages,
        { role: 'user', content: `FILE||${file.name}` }, // Add file message with filename
      ];
    }
  
    // Validate the structure of the messages array
    const isValidMessageContent = (content) =>
      typeof content === 'string' || Array.isArray(content);
  
    const validMessages = updatedMessages.map((msg) => ({
      ...msg,
      content: isValidMessageContent(msg.content) ? msg.content : JSON.stringify(msg.content), // Convert to string if invalid
    }));
  
    setMessages(validMessages);
    setMessage('');
    setSending(true); // Set sending to true to show loader
  
    try {
      const chatId = content.chatId;
  
      const formData = new FormData();
      formData.append('appName', content.appName);
      formData.append('description', content.description);
      formData.append('messages', JSON.stringify(validMessages)); // Send the updated messages
      formData.append('chatId', chatId);
      formData.append('userId', userId);
      formData.append('entryMessage', state.entryMessage);
      formData.append('title', state.title);
      formData.append('route', state.route);
      formData.append('documentChunks', JSON.stringify(state.documentChunks));
      if (file) {
        formData.append('document', file); // Add the file if it exists
      }
  
      const res = await axios.post(`${API}/chat/${state.route}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      const data = res.data;
  
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'assistant', content: data.reply },
      ]);
      updateData(data);
      updateSelectedId(content.chatId);
      setFile(null); // Clear file after sending
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response && error.response.data.error) {
          // Show the error message from the server
          setFileError(error.response.data.error);
          setShowSnackbar(true);
      }
     } finally {
      setSending(false); // Hide loader after sending
    }
  };
  


  useEffect(() => {
    updateSelectedId(content.chatId);
    setTitle(content.appName);
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (!content) {
    return <Typography>Loading...</Typography>;
  }

  if (translating) {
    return (<Box justifyContent="center" textAlign='center' padding={10}><Typography>Translating</Typography> <CircularProgress/></Box>);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        paddingTop: 10,
        paddingLeft: { xs: drawerWidth === 0 ? 38 : 0, sm: drawerWidth === 0 ? 38 : 0 },
        paddingRight: { xs: 0, sm: 0 },
      
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 160px)',
          paddingBottom: 10,
        }}
      >
        <Box display="flex" alignItems="flex-start">
          <Avatar sx={{ marginRight: 1, backgroundColor: theme.palette.primary.main, }}>
            <HubIcon />
          </Avatar>
          <Typography key={0} variant="body1" sx={{ margin: '10px 0', textAlign: 'left'}}>
            <div dangerouslySetInnerHTML={{ __html: content.entryMessage }} />
          </Typography>
        </Box>

        {/* Display messages */}
        {messages.map((msg, index) => (
  index !== 0 && (
    <Box sx={{marginRight:{ xs: 1, sm: 0 }}} key={index}>
      {/* Log to verify role and content */}
      {console.log(`Role: ${msg.role}, Content: ${msg.content}`)}

      {/* Handle User Text Messages */}
      {msg.role === 'user' && !msg.content.includes("FILE||") ? (
        <>
          {console.log("Rendering User Message")}
          <User msg={msg.content} />
        </>
      ) : null}

      {/* Handle File Messages */}
      {msg.role === 'user' && msg.content.includes("FILE||") ? (
        <>
          {console.log("Rendering File Message", msg.content.split("FILE||")[1])}
          <FileRole 
            fileName={msg.content.split("FILE||")[1]}  // Extract and display file name
          />
        </>
      ) : null}

      {/* Handle Assistant Messages */}
      {msg.role === 'assistant' ? (
        <>
          {console.log("Rendering Assistant Message")}
          <System msg={msg.content} />
        </>
      ) : null}
    </Box>
  )
))}

        {/* Loader for when a message is being sent */}
        {sending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: drawerWidth },
          right: 0,
          padding: { xs: '5px 10px', sm: '10px' },
          backgroundColor:theme.palette.appbar,
          zIndex: 1000,
          boxShadow: '0 -1px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        {file && (
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <FileChip handleRemoveFile={handleRemoveFile} file={file} />
          </Box>
        )}
         <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <TextField
        placeholder="Type your message..."
        multiline
        minRows={1}
        maxRows={6}
        fullWidth
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        variant="outlined"
        sx={{
          fontSize: '0.9rem',
          bgcolor: theme.palette.mode === 'dark' ? '#2e2e2e' : '#F5F5F5', // Adjust based on mode
        }}
      />
      <input
        type="file"
        id="file-input"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <label htmlFor="file-input">
        <IconButton component="span" sx={{ marginLeft: '10px' }}>
          <AttachFileIcon color={theme.palette.mode === 'dark' ? 'action' : 'inherit'} />
        </IconButton>
      </label>
      <IconButton
        color="primary"
        onClick={sendMessage}
        disabled={sending}
        sx={{
          marginLeft: '10px',
          position: 'relative',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }}
      >
        <SendIcon />
        {sending && (
          <CircularProgress
            size={24}
            sx={{
              position: 'absolute',
              color: theme.palette.primary.contrastText,
            }}
          />
        )}
      </IconButton>
    </Box>
      </Box>

      {/* Snackbar for file errors */}
      <Snackbar open={showSnackbar} autoHideDuration={4000} onClose={() => setShowSnackbar(false)}>
        <Alert onClose={() => setShowSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {fileError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
